import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import { get } from 'lodash-es';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { BuyingTipsEntities } from 'client/data/models/buying-tips';
import { CoreLeaseDealsModel, CoreLeaseDealsPaths } from 'client/data/models/core-lease-deals';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
  InventoryTrimSummaryEntities,
} from 'client/data/models/inventory-core-configurations';
import { PageModel } from 'client/data/models/page';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { TrackingConstant } from 'client/tracking/constant';
import { EventToolbox } from 'client/utils/event-toolbox';
import { getCustomCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { getSuggestedDisplayPriceFromTrim } from 'site-modules/shared/utils/core-page/suggested-price';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { getCurrentMonth } from 'site-modules/shared/utils/time-util';
import { TransparentPricingLeadFormDrawer } from 'site-modules/shared/components/lead-form/transparent-pricing-lead-form/transparent-pricing-lead-form-drawer';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { Link } from 'site-modules/shared/components/link/link';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { ANSWER, ThumbsSurvey } from 'site-modules/shared/components/thumbs-survey/thumbs-survey';
import { ReviewSection } from 'site-modules/core-page/components/review-section/review-section';

import './buying-tips.scss';

const EST_SAVINGS = 'Est. Savings';

export function BuyingTips({
  vehicleName,
  params = null,
  buyingTips = null,
  bestLeaseDeal = null,
  buyingTipsText = null,
  sharedTrimName = null,
  trims = null,
  vehicle = null,
  visitorLocation = null,
  pageName = null,
  isMobile = false,
  isCore7809Enabled = false,
}) {
  const [isLeadFormOpen, toggleLeadFormOpen] = useToggle(false);

  const hasBuyingTips = !!buyingTipsText;
  useEffect(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        synpartner: TrackingConstant.SYNPARTNER,
        action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
        page_name: 'new_model_core',
        page_category: 'new_model_core',
        subaction_name: 'show_buying_tips',
        value: hasBuyingTips ? 'yes' : 'no',
      },
    });
  }, [hasBuyingTips]);

  if (!hasBuyingTips) {
    return null;
  }

  const core7809Cards = isCore7809Enabled
    ? [
        {
          label: EST_SAVINGS,
          value:
            buyingTips?.edmunds_calc_savings_off_msrp_model > 499 &&
            getPriceStringV2(buyingTips?.edmunds_calc_savings_off_msrp_model, ''),
          onClick: toggleLeadFormOpen,
          trackingId: 'esp_tip_pricing_drawer',
        },
        {
          label: 'Avg Lease Payment',
          value: getPriceStringV2(bestLeaseDeal?.payment || buyingTips?.median_monthly_lease_payment_model, ''),
          url:
            bestLeaseDeal?.payment && bestLeaseDeal?.id
              ? `/lease-deals/?id=${bestLeaseDeal.id}`
              : `/${params?.make}-${params?.model}-lease-deals/`,
          target: '_blank',
          trackingId: 'lease_tip_srp_link',
        },
        {
          label: 'Cash Rebate',
          value: getPriceStringV2(buyingTips?.median_primary_customer_cash_model, ''),
          url: getCustomCoreUrl(
            { makeSlug: params?.make, modelSlug: params?.model, year: params?.year },
            'deals?defaultSection=rebates#incentives-rebates'
          ),
          target: '_blank',
          trackingId: 'rebate_tip_deals_link',
        },
        {
          label: 'Special APR',
          value: buyingTips?.median_apr_special_60 && `${buyingTips?.median_apr_special_60}%`,
          valueText: '60 mo',
          url: getCustomCoreUrl(
            { makeSlug: params?.make, modelSlug: params?.model, year: params?.year },
            'deals?defaultSection=financing#incentives-financing'
          ),
          target: '_blank',
          trackingId: 'apr_tip_deals_link',
        },
      ].filter(({ value }) => value)
    : [];
  const hasPricingDrawer = core7809Cards.some(({ label }) => label === EST_SAVINGS);

  const currentTrim = (trims && trims.find(({ trimName }) => trimName === sharedTrimName)) || get(trims, '[0]');
  const dataTrackingLeadForm = getLeadFormCreativeId({
    isNewVin: true,
    pageNameLegacy: pageName,
    formName: LEAD_FORMS_CREATIVE_ID.BUYING_TIPS,
  });

  const buyingTipsFragment = (
    <ReviewSection
      body={buyingTipsText}
      heading={
        <Fragment>
          {isCore7809Enabled && 'Deals & '}Buying Tips for the {vehicleName} for {getCurrentMonth()}{' '}
          <PopoverItem
            iconClassName="size-16"
            openTrackingParams={{
              subaction_name: 'view_buying_tips_tooltip',
              creative_id: 'edm-buying-tips',
            }}
            screenReaderTooltipLabel="Buying tips"
          >
            Manufacturer offers are subject to change and may vary by region. Please verify all incentives with your
            dealer.
          </PopoverItem>
        </Fragment>
      }
      className={isCore7809Enabled ? 'bg-white px-1 py-1 rounded-12' : undefined}
      subheadClassName="medium"
      headingClassName={classnames('heading-3', { 'mt-1_5': !isCore7809Enabled })}
      bodyCreativeId="edm-buying-tips"
      afterBodyChildren={
        <Fragment>
          <hr className="mt-1 mb-1" />
          <ThumbsSurvey
            question="Was this information helpful?"
            creativeId="pricing-cards-survey"
            className={classnames('d-flex flex-row align-items-center size-16', { 'mb-1_5': !isCore7809Enabled })}
            questionClassName="me-1"
            answerWrapperClassName="d-flex"
            answerButtonClassName="me-1 mx-md-0_5"
            textQuestionClassName={classnames({ 'text-nowrap': !isMobile, 'd-none': isMobile })}
            textAreaClassName="size-16 rounded-8 me-0_75 ms-md-1_5 me-md-1 py-0_5"
            textSubmitClassName="size-16 rounded-8 py-0_25"
            textAriaPlaceholder={isMobile ? 'Please tell us why' : ''}
            thankYouClassName=""
            textAreaRowCount={1}
            isTextFormOneLine
            trackingMap={{
              submit: 'submit_pricing_cards_survey',
              [ANSWER.YES]: 'pricing_cards_survey_yes_comments',
              [ANSWER.NO]: 'pricing_cards_survey_no_comments',
            }}
          />
        </Fragment>
      }
    >
      {!!core7809Cards?.length && (
        <Fragment>
          {
            <Row tag="ul" className="list-unstyled d-flex gy-1 pt-0_5">
              {core7809Cards.map(({ label, value, valueText, url, onClick, target, trackingId }) => {
                const ButtonTag = onClick ? 'button' : Link;

                return (
                  <Col xs={6} md="auto" tag="li" className="d-flex flex-md-fill" key={label}>
                    <ButtonTag
                      to={url}
                      onClick={onClick}
                      target={target}
                      className="tip-btn flex-fill rounded-8 px-0_5 py-0_75 d-flex flex-column justify-content-center align-items-center text-center gap-0_25 text-decoration-none size-14 bg-white"
                      data-tracking-id={trackingId}
                    >
                      {label}
                      <span className="d-flex align-items-baseline gap-0_25 size-20 text-blue-30 fw-bold">
                        <span className="label-text">{value}</span>
                        {!!valueText && <span className="fw-normal size-14">{valueText}</span>}
                        <i className="icon-arrow-right3 align-self-center size-12" aria-hidden />
                      </span>
                    </ButtonTag>
                  </Col>
                );
              })}
            </Row>
          }
          {hasPricingDrawer && (
            <TransparentPricingLeadFormDrawer
              isOpen={isLeadFormOpen}
              onDrawerToggle={toggleLeadFormOpen}
              params={{
                styleId: currentTrim?.styleId,
                trim: currentTrim?.trimName,
                vehicle,
                zipCode: visitorLocation?.zipCode,
                city: visitorLocation?.city,
              }}
              options={{
                isMobile,
                bestDealPrice: getSuggestedDisplayPriceFromTrim(currentTrim),
                estimatedSavings: currentTrim?.pricing?.estimatedSavings,
                creativeId: dataTrackingLeadForm,
              }}
              creativeId={dataTrackingLeadForm}
            />
          )}
        </Fragment>
      )}
    </ReviewSection>
  );

  return isCore7809Enabled ? (
    <div className="buying-tips bg-cool-gray-100 px-1 py-1 rounded-12 mt-2">{buyingTipsFragment}</div>
  ) : (
    buyingTipsFragment
  );
}

// TODO: Review props after core-7809
BuyingTips.propTypes = {
  vehicleName: PropTypes.string.isRequired,
  params: CorePageParams,
  buyingTips: BuyingTipsEntities.BuyingTipsSummary,
  bestLeaseDeal: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    payment: PropTypes.number,
  }),
  buyingTipsText: PropTypes.string,
  sharedTrimName: PropTypes.string,
  trims: InventoryTrimSummaryEntities.TrimSummaries,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  visitorLocation: VisitorEntities.Location,
  pageName: PropTypes.string,
  isMobile: PropTypes.bool,
  isCore7809Enabled: PropTypes.bool,
};

const mapStateToProps = state => ({
  pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const propsAreEqual = (prevProps, { trims }) => !trims;
export const BuyingTipsCore7809 = connect(mapStateToProps)(
  connectToModel(React.memo(BuyingTips, propsAreEqual), {
    visitorLocation: bindToPath('location', VisitorModel),
    bestLeaseDeal: bindToPath(
      ({ vehicle }) =>
        CoreLeaseDealsPaths.getFirstBestPaymentDealMMYPath({
          make: vehicle.make.name,
          model: vehicle.model.name,
          year: vehicle.year,
        }),
      CoreLeaseDealsModel,
      deals => deals?.best
    ),
    sharedTrimName: bindToPath('sharedValues.trimName', PageModel),
    trims: bindToPath(
      ({ vehicle }) => buildTrimSummariesPath(getParamsFromVehicle(vehicle)),
      InventoryCoreConfigurationsModel
    ),
  })
);
